/**
 * Facility Display Name / Legal Name rules shared by the Sage facilities sync
 * and the file importers (ADR 0021).
 *
 * The database is the authority on uniqueness (`lower(btrim(...))` indexes plus
 * the `facilities_names_cross_unique` trigger). {@link facilityNameKey} is the
 * in-memory twin used to pre-check collisions and to match import cells. It
 * trims all whitespace where SQL `btrim` trims only spaces, so it can only call
 * two names "equal" more often than the database does — the safe direction:
 * a false positive becomes a suffix or an explicit ambiguity error, never a
 * silent wrong match.
 */

/**
 * Longest Display Name a user may enter (DCP-367). Product limit, not a layout
 * guarantee: at 1280px a realistic 14-character name is the most that fits the
 * Cash Position column header without an ellipsis. Not enforced in the database
 * yet, because existing Display Names can be longer.
 */
export const FACILITY_DISPLAY_NAME_MAX_LENGTH = 40

/** Case- and surrounding-whitespace-insensitive comparison key for a facility name. */
export function facilityNameKey(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * The first two whitespace-separated words of a name ('' for a blank name).
 * Punctuation left dangling at the cut is dropped ("Kesser Property, LLC" →
 * "Kesser Property"); punctuation inside a word is kept ("Sebo's Casa").
 */
export function firstTwoWords(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join(' ')
    .replace(/[,;:.]+$/, '')
}

/**
 * Default Display Name for a facility imported from Sage (DCP-367): the first
 * two words of its Legal Name, or the LOCATIONID when the name is blank.
 *
 * When that name is already taken (`isTaken` receives a {@link facilityNameKey})
 * the LOCATIONID is appended — Sage LOCATIONIDs are unique, so
 * `"The Lev (601)"` cannot collide with `"The Lev (602)"`. The second form is
 * not re-checked: if it is taken too the database rejects the insert and the
 * sync reports it like any other failed row.
 */
export function deriveDisplayName(
  legalName: string,
  locationId: string,
  isTaken: (key: string) => boolean,
): { displayName: string; suffixed: boolean } {
  const id = locationId.trim()
  const base = fitDisplayName(firstTwoWords(legalName) || id, 0)
  if (!isTaken(facilityNameKey(base))) return { displayName: base, suffixed: false }
  const suffix = ` (${id})`
  return { displayName: `${fitDisplayName(base, suffix.length)}${suffix}`, suffixed: true }
}

/** Cuts `name` so it plus `reserved` characters fits FACILITY_DISPLAY_NAME_MAX_LENGTH. */
function fitDisplayName(name: string, reserved: number): string {
  const room = Math.max(FACILITY_DISPLAY_NAME_MAX_LENGTH - reserved, 1)
  return name.length <= room ? name : name.slice(0, room).trimEnd()
}
